import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type ColumnDef,
  type ColumnOrderState,
  type ColumnSizingState,
  type Header,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
} from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { ChevronUp, ChevronDown, ChevronRight, GripVertical, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils/cn'
import { usePreferences } from '@/shell/stores/preferences'
import { EmptyState } from '../EmptyState'
import { QueryErrorState } from '../QueryErrorState'
import type { GroupAccent, TableGroup } from './grouping'

export interface DataTableProps<T> {
  data: T[]
  columns: ColumnDef<T, unknown>[]
  /** Sticky-left identifier column. Default true. */
  stickyFirstColumn?: boolean
  /** Predicate for critical rows → 2px red left-border accent. */
  criticalRowPredicate?: (row: T) => boolean
  /** Click anywhere on the row to invoke. */
  onRowClick?: (row: T) => void
  /** Selection — external controlled via RowSelectionState. */
  selection?: RowSelectionState
  onSelectionChange?: (s: RowSelectionState) => void
  /** Virtualize when rows exceed this threshold. Default 200. */
  virtualizeAfter?: number
  /** Estimated row height for the virtualizer in px (fall back, density tokens win). */
  estimatedRowHeight?: number
  /** Visible-rows hint — used by the skeleton renderer. */
  loading?: boolean
  /** Override the default empty state. */
  emptyState?: React.ReactNode
  /**
   * Rejected value from the query feeding `data`. When set it takes
   * precedence over both `loading` and `emptyState`: a failed fetch must
   * never render as "no matching rows", because an empty table reads as
   * "there is nothing here" / "you lack access" and hides the failure.
   */
  error?: unknown
  /** Retry handler for the error state — wire to the query's `refetch`. */
  onRetry?: () => void
  /** What the table lists, lowercase ("open orders") — used in error copy. */
  errorSubject?: string
  /** Optional wrapper className (the border / rounded shell). */
  className?: string
  /** Stable row-id accessor. Defaults to index-as-string. */
  getRowId?: (row: T, index: number) => string
  /** Max scroll viewport height. Default 100% — fills a flex/h-full page well. */
  maxHeight?: string
  /**
   * Fixed column widths. When provided, the table switches to
   * `table-layout: fixed` and renders a `<colgroup>` — pixel widths for
   * constrained columns, `undefined` for columns that should flex.
   * Keys must align to `column.id` from the TanStack column defs.
   * Also sets a table `min-width` so horizontal scroll engages before
   * columns collapse.
   */
  columnWidths?: Record<string, number | undefined>
  /** Minimum table width in px — triggers horizontal scroll. Default 900. */
  minTableWidth?: number
  /**
   * Module identifier for per-user column-state persistence (Sprint 17).
   * When provided, the table will persist column resize/reorder under
   * `UserPreferences.tableColumns[moduleId]`. Chunk 1 only adds the
   * resize primitive; persistence wires up in chunk 3.
   */
  moduleId?: string
  /**
   * Enable drag-to-resize column widths (Sprint 17 chunk 1).
   * Defaults to `true` when `moduleId` is provided, `false` otherwise —
   * opt-in until consumers migrate. The 4px hover-revealed grip on the
   * right edge of each column header dispatches a TanStack-managed
   * resize. Resized widths persist locally for the session; prefs-backed
   * persistence lands in chunk 3.
   */
  enableColumnResize?: boolean
  /**
   * Enable drag-to-reorder columns (Sprint 17 chunk 2).
   * Defaults to `true` when `moduleId` is provided, `false` otherwise.
   * Hover-revealed drag handle (GripVertical icon) on the left edge of
   * each column header. Activation distance 6px disambiguates click
   * from drag (matches Sprint 13b sidebar reorder).
   */
  enableColumnReorder?: boolean
  /**
   * Grouped render mode. When provided, the table renders one collapsible
   * full-width header row per group (in the given order) followed by that
   * group's rows, all inside the same `<table>` so columns stay aligned.
   * Empty groups still render their header (with a `(0)` count) — that's
   * what surfaces stages that currently have no rows. The grouping must be
   * a partition of `data` (same object references); the flat row model is
   * still built from `data`, so sort / selection / column state keep
   * working. Virtualization is disabled while grouped. Flat mode (no
   * `groups`) is unchanged.
   */
  groups?: TableGroup<T>[]
  /** Controlled set of collapsed group keys. Omit to let the table manage it. */
  collapsedGroups?: ReadonlySet<string>
  /** Fired when a group header chevron is clicked. */
  onToggleGroup?: (key: string) => void
  /**
   * Row ids (matching `getRowId`) whose expanded content should render as
   * an extra full-width row immediately below them. Omit / leave empty to
   * disable expansion entirely. Virtualization is disabled while any row
   * is expanded, same as grouped mode — expanded content has variable
   * height the virtualizer's fixed `estimatedRowHeight` can't account for.
   */
  expandedRowIds?: ReadonlySet<string>
  /** Renders the content of the extra row inserted below an expanded row. */
  renderExpandedRow?: (row: T) => React.ReactNode
  /**
   * Sort applied on first render (e.g. `[{ id: 'pct_change', desc: true }]`),
   * so a default order shows its header arrow and the user can click away
   * from it. Read once; later changes are ignored.
   */
  initialSorting?: SortingState
  /** Uppercase, tracked header labels (the default). `false` renders
   *  them in the case written in the column defs. */
  uppercaseHeaders?: boolean
}

/**
 * Canonical DataTable — every DataView-archetype module composes this.
 *
 * Features implemented in M3:
 * - Sticky header + first column (config)
 * - Sort via column def (TanStack's getSortedRowModel)
 * - Row selection via TanStack's built-in model (checkbox column optional
 *   via a column def with `id: '__select'`; keyboard `x` is the module's
 *   concern — wired into the shell in M5)
 * - Critical-row left accent via inset box-shadow on the sticky cell
 * - Virtualization for > `virtualizeAfter` rows (default 200)
 * - Loading skeleton + empty state pass-through
 *
 * Toolbar / footer live above/below the table — compose with <div> wrappers
 * to keep this primitive focused on rendering the grid itself.
 */
export function DataTable<T>({
  data,
  columns,
  stickyFirstColumn = true,
  criticalRowPredicate,
  onRowClick,
  selection,
  onSelectionChange,
  virtualizeAfter = 200,
  estimatedRowHeight = 40,
  loading = false,
  emptyState,
  error,
  onRetry,
  errorSubject,
  className,
  getRowId,
  maxHeight = '100%',
  columnWidths,
  minTableWidth = 900,
  moduleId,
  enableColumnResize,
  enableColumnReorder,
  groups,
  collapsedGroups,
  onToggleGroup,
  expandedRowIds,
  renderExpandedRow,
  initialSorting,
  uppercaseHeaders = true,
}: DataTableProps<T>) {
  const grouped = groups !== undefined
  const resizeEnabled = enableColumnResize ?? Boolean(moduleId)
  const reorderEnabled = enableColumnReorder ?? Boolean(moduleId)

  // Uncontrolled collapse fallback so grouped mode works without a parent
  // wiring `collapsedGroups`/`onToggleGroup` (used in tests + simple cases).
  const [internalCollapsed, setInternalCollapsed] = useState<ReadonlySet<string>>(
    () => new Set<string>(),
  )
  const collapsedSet = collapsedGroups ?? internalCollapsed
  const toggleGroup = (key: string) => {
    if (onToggleGroup) {
      onToggleGroup(key)
      return
    }
    setInternalCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  // Sprint 17 chunk 3 — read persisted column sizing from user prefs.
  // `usePreferences` is a Zustand store; the selector returns a stable
  // reference for the same content, so this won't churn renders. Until
  // hydrate completes, persisted is undefined and we render with the
  // prop seed. After hydrate, the sync effect below picks up persisted
  // values once.
  const persistedSizing = usePreferences((s) =>
    moduleId ? s.tableColumns[moduleId]?.sizing : undefined,
  )
  const persistedOrder = usePreferences((s) =>
    moduleId ? s.tableColumns[moduleId]?.order : undefined,
  )
  const hydrated = usePreferences((s) => s.hydrated)
  const setTableColumnSizing = usePreferences((s) => s.setTableColumnSizing)
  const setTableColumnOrder = usePreferences((s) => s.setTableColumnOrder)
  const resetTableColumns = usePreferences((s) => s.resetTableColumns)
  const hasCustomState = Boolean(
    moduleId &&
      ((persistedSizing && Object.keys(persistedSizing).length > 0) ||
        (persistedOrder && persistedOrder.length > 0)),
  )

  const [sorting, setSorting] = useState<SortingState>(() => initialSorting ?? [])

  // Seed initial column sizing from the columnWidths prop. The prop
  // becomes the *default* layout that a fresh visitor sees; user
  // resizes mutate this state. After hydrate, persisted prefs (if any)
  // override via the effect below.
  const initialColumnSizing = useMemo<ColumnSizingState>(() => {
    if (!columnWidths) return {}
    const out: ColumnSizingState = {}
    for (const [id, w] of Object.entries(columnWidths)) {
      if (typeof w === 'number') out[id] = w
    }
    return out
  }, [columnWidths])
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(initialColumnSizing)

  // Sprint 17 chunk 3 — sync persisted prefs into TanStack state on
  // first hydrate. Subsequent user resizes flow through
  // `onColumnSizingChange` → `setTableColumnSizing` → store, then the
  // selector returns the same content we just wrote, so this effect
  // is bounded to a one-time apply. The ref guard prevents bouncing
  // when persisted reference changes via our own write.
  const didApplyPersistedRef = useRef(false)
  useEffect(() => {
    if (didApplyPersistedRef.current) return
    if (!hydrated) return
    didApplyPersistedRef.current = true
    if (persistedSizing && Object.keys(persistedSizing).length > 0) {
      // Merge persisted on top of seeded defaults so columns the user
      // hasn't resized still respect the consumer's columnWidths prop.
      setColumnSizing((prev) => ({ ...prev, ...persistedSizing }))
    }
  }, [hydrated, persistedSizing])

  // Sprint 17 chunk 2 — column order. Initial state is empty (TanStack
  // falls back to the column-defs declaration order). User reorders
  // populate it; persisted order applies once on hydrate. Order ids
  // reference column.id from the columns prop.
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([])
  const didApplyOrderRef = useRef(false)
  useEffect(() => {
    if (didApplyOrderRef.current) return
    if (!hydrated) return
    didApplyOrderRef.current = true
    if (persistedOrder && persistedOrder.length > 0) {
      setColumnOrder([...persistedOrder])
    }
  }, [hydrated, persistedOrder])

  // 6px activation distance matches Sprint 13b sidebar reorder. Keeps
  // a click on the header (sort) from being mis-read as the start of
  // a drag. The right-edge resize handle is a separate event target,
  // unaffected by this sensor.
  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  )

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    state: { sorting, rowSelection: selection ?? {}, columnSizing, columnOrder },
    onSortingChange: setSorting,
    onColumnOrderChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnOrder) : updater
      setColumnOrder(next)
      if (moduleId) {
        setTableColumnOrder(moduleId, next)
      }
    },
    onColumnSizingChange: (updater) => {
      const next =
        typeof updater === 'function' ? updater(columnSizing) : updater
      setColumnSizing(next)
      // Persist when a moduleId is provided. The store debounces the
      // network write (500ms via `schedulePersist`), so live drag
      // doesn't hammer the API — only the final paused width persists.
      if (moduleId) {
        setTableColumnSizing(moduleId, next)
      }
    },
    onRowSelectionChange: onSelectionChange
      ? (updater) => {
          const next = typeof updater === 'function' ? updater(selection ?? {}) : updater
          onSelectionChange(next)
        }
      : undefined,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: getRowId ? (row, index) => getRowId(row, index) : (_row, index) => String(index),
    enableRowSelection: Boolean(onSelectionChange),
    enableColumnResizing: resizeEnabled,
    columnResizeMode: 'onChange',
    // 40px floor per the spec's "no individual column smaller than 40px"
    // rule. Upper cap is generous — wide cells like Reason or Notes
    // benefit from being draggable past 600px.
    defaultColumn: { minSize: 40, maxSize: 800 },
  })

  const rows = table.getRowModel().rows
  const hasExpandedRows = Boolean(expandedRowIds && expandedRowIds.size > 0)
  const shouldVirtualize = !grouped && !hasExpandedRows && rows.length > virtualizeAfter

  // Grouped mode: map each row back to its source object so groups (a
  // partition of `data` by reference) can look up the already-built
  // TanStack Row, and sort group items by the current sorted position so
  // column sort still applies within each group.
  const rowByOriginal = useMemo(() => {
    if (!grouped) return null
    const m = new Map<T, Row<T>>()
    rows.forEach((r) => m.set(r.original, r))
    return m
  }, [grouped, rows])
  const orderIndex = useMemo(() => {
    if (!grouped) return null
    const m = new Map<T, number>()
    rows.forEach((r, i) => m.set(r.original, i))
    return m
  }, [grouped, rows])

  const scrollRef = useRef<HTMLDivElement>(null)
  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan: 8,
  })

  const virtualRows = shouldVirtualize ? virtualizer.getVirtualItems() : null
  const paddingTop = virtualRows?.length ? virtualRows[0]!.start : 0
  const paddingBottom = virtualRows?.length
    ? virtualizer.getTotalSize() - virtualRows[virtualRows.length - 1]!.end
    : 0

  // A full-width state (empty / error) lives in a `<td colSpan>`, so its
  // cell is as wide as the table — up to `minTableWidth`, often 2000px+.
  // Centering inside that cell pushes the content far off the right edge
  // of the scroll viewport. Pin it to the viewport instead and size it to
  // what's actually visible.
  const [viewportWidth, setViewportWidth] = useState<number | undefined>(undefined)
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    const measure = () => setViewportWidth(el.clientWidth || undefined)
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const hasError = error != null
  const isEmpty =
    !loading && !hasError && (grouped ? groups!.length === 0 : rows.length === 0)
  const leafColumnCount = table.getAllLeafColumns().length

  // Column id list in current display order — drives SortableContext.
  // table.getAllLeafColumns() respects columnOrder state when set.
  const orderedColumnIds = table.getAllLeafColumns().map((c) => c.id)

  function onColumnDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = orderedColumnIds
    const oldIndex = ids.indexOf(String(active.id))
    const newIndex = ids.indexOf(String(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const next = [...ids]
    const [moved] = next.splice(oldIndex, 1)
    next.splice(newIndex, 0, moved!)
    table.setColumnOrder(next)
  }

  function handleResetColumns() {
    if (!moduleId) return
    resetTableColumns(moduleId)
    // Reset local TanStack state too — the prefs-sync effect only runs
    // once on first hydrate, so subsequent reset wouldn't bounce back
    // into local state otherwise.
    setColumnSizing(initialColumnSizing)
    setColumnOrder([])
  }

  const tableMarkup = (
    <div
      className={cn(
        'flex h-full min-h-0 flex-col overflow-hidden rounded-md border border-border-subtle bg-surface',
        className,
      )}
    >
      {hasCustomState ? (
        <div className="flex items-center justify-end border-b border-border-subtle bg-surface-sunk px-2 py-1 text-2xs">
          <button
            type="button"
            onClick={handleResetColumns}
            className={cn(
              'inline-flex cursor-pointer items-center gap-1 rounded-xs border-none bg-transparent px-1.5 py-0.5 text-tertiary',
              'hover:bg-surface hover:text-primary',
              'focus:outline-none focus:shadow-focus',
            )}
            aria-label="Reset columns to defaults"
            title="Reset column order + widths to module defaults"
          >
            <RotateCcw size={10} aria-hidden="true" />
            <span>Reset columns</span>
          </button>
        </div>
      ) : null}
      <div
        ref={scrollRef}
        className="relative min-h-0 flex-1 overflow-auto"
        style={{ maxHeight }}
      >
        <table
          className={cn(
            'w-full border-separate border-spacing-0 text-sm',
            columnWidths && 'table-fixed',
          )}
          style={columnWidths ? { minWidth: `${minTableWidth}px` } : undefined}
        >
          {columnWidths ? (
            <colgroup>
              {table.getAllLeafColumns().map((col) => {
                // Resize on: prefer TanStack's tracked size (seeded from
                // columnWidths, mutated by user resize). Off: original
                // prop-driven behavior — undefined means flex.
                const w = resizeEnabled
                  ? columnSizing[col.id]
                  : columnWidths[col.id]
                return (
                  <col
                    key={col.id}
                    style={w !== undefined ? { width: `${w}px` } : undefined}
                  />
                )
              })}
            </colgroup>
          ) : null}
          <thead>
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header, idx) => (
                  <SortableHeaderCell
                    key={header.id}
                    header={header}
                    sticky={stickyFirstColumn && idx === 0}
                    resizeEnabled={resizeEnabled}
                    reorderEnabled={reorderEnabled}
                    uppercase={uppercaseHeaders}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {loading && !hasError
              ? Array.from({ length: 6 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    {table.getAllLeafColumns().map((col, idx) => (
                      <td
                        key={col.id}
                        className={cn(
                          'h-[var(--row-height)] border-b border-border-subtle px-[var(--cell-padding-x)]',
                          stickyFirstColumn && idx === 0 && 'sticky left-0 bg-surface',
                        )}
                      >
                        <span className="inline-block h-3 w-full rounded-xs bg-muted" />
                      </td>
                    ))}
                  </tr>
                ))
              : null}
            {hasError ? (
              <tr>
                <td colSpan={table.getAllLeafColumns().length}>
                  <div className="sticky left-0" style={{ width: viewportWidth }}>
                    <QueryErrorState
                      error={error}
                      onRetry={onRetry}
                      subject={errorSubject ?? 'these rows'}
                      className="border-0"
                    />
                  </div>
                </td>
              </tr>
            ) : null}
            {isEmpty ? (
              <tr>
                <td colSpan={table.getAllLeafColumns().length}>
                  <div className="sticky left-0" style={{ width: viewportWidth }}>
                    {emptyState ?? <EmptyState variant="no-match" title="No matches" />}
                  </div>
                </td>
              </tr>
            ) : null}
            {grouped && !isEmpty
              ? groups!.map((group) => {
                  const collapsed = collapsedSet.has(group.key)
                  const items = [...group.items].sort(
                    (a, b) => (orderIndex!.get(a) ?? 0) - (orderIndex!.get(b) ?? 0),
                  )
                  return (
                    <GroupRows
                      key={group.key}
                      groupKey={group.key}
                      label={group.label}
                      accent={group.accent}
                      count={group.items.length}
                      collapsed={collapsed}
                      colSpan={leafColumnCount}
                      onToggle={() => toggleGroup(group.key)}
                    >
                      {collapsed
                        ? null
                        : items.map((item) => {
                            const row = rowByOriginal!.get(item)
                            if (!row) return null
                            return renderRow({
                              row,
                              stickyFirstColumn,
                              criticalRowPredicate,
                              onRowClick,
                              expandedRowIds,
                              renderExpandedRow,
                            })
                          })}
                    </GroupRows>
                  )
                })
              : null}
            {!grouped && shouldVirtualize ? (
              <>
                {paddingTop > 0 ? (
                  <tr aria-hidden="true" style={{ height: paddingTop }}>
                    <td colSpan={table.getAllLeafColumns().length} />
                  </tr>
                ) : null}
                {virtualRows!.map((vr) => {
                  const row = rows[vr.index]!
                  return renderRow({
                    row,
                    stickyFirstColumn,
                    criticalRowPredicate,
                    onRowClick,
                    expandedRowIds,
                    renderExpandedRow,
                  })
                })}
                {paddingBottom > 0 ? (
                  <tr aria-hidden="true" style={{ height: paddingBottom }}>
                    <td colSpan={table.getAllLeafColumns().length} />
                  </tr>
                ) : null}
              </>
            ) : !grouped ? (
              rows.map((row) =>
                renderRow({
                  row,
                  stickyFirstColumn,
                  criticalRowPredicate,
                  onRowClick,
                  expandedRowIds,
                  renderExpandedRow,
                }),
              )
            ) : null}
          </tbody>
        </table>
      </div>
    </div>
  )

  if (!reorderEnabled) {
    return tableMarkup
  }

  return (
    <DndContext sensors={dndSensors} onDragEnd={onColumnDragEnd}>
      <SortableContext items={orderedColumnIds} strategy={horizontalListSortingStrategy}>
        {tableMarkup}
      </SortableContext>
    </DndContext>
  )
}

interface SortableHeaderCellProps<T> {
  header: Header<T, unknown>
  sticky: boolean
  resizeEnabled: boolean
  reorderEnabled: boolean
  uppercase: boolean
}

function SortableHeaderCell<T>({
  header,
  sticky,
  resizeEnabled,
  reorderEnabled,
  uppercase,
}: SortableHeaderCellProps<T>) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: header.column.id, disabled: !reorderEnabled })

  const canSort = header.column.getCanSort()
  const sortDir = header.column.getIsSorted()
  const isNumeric = header.column.columnDef.meta?.numeric === true
  const canResize = resizeEnabled && header.column.getCanResize()
  const isResizing = header.column.getIsResizing()
  const labelText =
    typeof header.column.columnDef.header === 'string'
      ? header.column.columnDef.header
      : 'column'

  const sortableStyle: React.CSSProperties = reorderEnabled
    ? {
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        transition,
        // Lift the dragged column above peers + the sticky-first sibling
        // so it visibly tracks the cursor instead of being clipped by
        // overflow-hidden on neighboring ths.
        zIndex: isDragging ? 50 : undefined,
        opacity: isDragging ? 0.85 : undefined,
      }
    : {}

  return (
    <th
      ref={reorderEnabled ? setNodeRef : undefined}
      scope="col"
      aria-sort={
        sortDir === 'asc'
          ? 'ascending'
          : sortDir === 'desc'
            ? 'descending'
            : canSort
              ? 'none'
              : undefined
      }
      style={sortableStyle}
      className={cn(
        'group/th relative sticky top-0 z-sticky overflow-hidden text-ellipsis whitespace-nowrap border-b border-border-default border-r border-r-border-subtle',
        'bg-surface-sunk px-[var(--cell-padding-x)] text-left font-semibold text-tertiary',
        uppercase ? 'text-2xs uppercase tracking-widest' : 'text-xs',
        'h-[var(--header-row-height)] last:border-r-0',
        isNumeric && 'text-right',
        sticky && 'sticky left-0 z-drawer',
        // Pad the left side a bit when reorder is on so the grip handle
        // doesn't overlap header text. Numeric columns keep their right-
        // aligned content; the grip stays on the absolute left edge.
        reorderEnabled && 'pl-5',
      )}
    >
      {reorderEnabled ? (
        <span
          {...attributes}
          {...listeners}
          aria-label={`Drag to reorder ${labelText}`}
          data-dragging={isDragging || undefined}
          className={cn(
            'absolute left-0 top-0 z-base inline-flex h-full w-4 cursor-grab items-center justify-center select-none touch-none text-tertiary',
            'opacity-0 group-hover/th:opacity-60 hover:!opacity-100',
            isDragging && 'cursor-grabbing opacity-100',
          )}
        >
          <GripVertical size={10} strokeWidth={2} aria-hidden="true" />
        </span>
      ) : null}
      {header.isPlaceholder ? null : canSort ? (
        <button
          type="button"
          onClick={header.column.getToggleSortingHandler()}
          className={cn(
            'inline-flex cursor-pointer items-center gap-1 border-none bg-transparent p-0 text-inherit',
            'hover:text-primary',
            sortDir && 'text-primary',
          )}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
          <span className="inline-flex flex-col opacity-60">
            <ChevronUp
              size={8}
              strokeWidth={3}
              className={cn(sortDir === 'asc' ? 'opacity-100' : 'opacity-40')}
            />
            <ChevronDown
              size={8}
              strokeWidth={3}
              className={cn(sortDir === 'desc' ? 'opacity-100' : 'opacity-40')}
            />
          </span>
        </button>
      ) : (
        flexRender(header.column.columnDef.header, header.getContext())
      )}
      {canResize ? (
        <span
          role="separator"
          aria-orientation="vertical"
          aria-label={`Resize ${labelText}`}
          data-resizing={isResizing || undefined}
          onMouseDown={header.getResizeHandler()}
          onTouchStart={header.getResizeHandler()}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'absolute right-0 top-0 z-base h-full w-1 cursor-col-resize select-none touch-none',
            'bg-border-default opacity-0',
            'group-hover/th:opacity-100',
            isResizing && 'opacity-100 bg-accent w-[2px]',
          )}
        />
      ) : null}
    </th>
  )
}

function renderRow<T>({
  row,
  stickyFirstColumn,
  criticalRowPredicate,
  onRowClick,
  expandedRowIds,
  renderExpandedRow,
}: {
  row: Row<T>
  stickyFirstColumn: boolean
  criticalRowPredicate?: (row: T) => boolean
  onRowClick?: (row: T) => void
  expandedRowIds?: ReadonlySet<string>
  renderExpandedRow?: (row: T) => React.ReactNode
}) {
  const isCritical = criticalRowPredicate ? criticalRowPredicate(row.original) : false
  const isSelected = row.getIsSelected()
  const isExpanded = Boolean(renderExpandedRow && expandedRowIds?.has(row.id))
  const visibleCells = row.getVisibleCells()
  return (
    <Fragment key={row.id}>
      <tr
        data-row-id={row.id}
        data-critical={isCritical || undefined}
        data-selected={isSelected || undefined}
        aria-selected={isSelected || undefined}
        onClick={onRowClick ? () => onRowClick(row.original) : undefined}
        className={cn(
          'group transition-colors duration-fast',
          onRowClick && 'cursor-pointer',
          'hover:bg-surface-hover',
          isSelected && 'bg-accent-subtle',
        )}
      >
        {visibleCells.map((cell, idx) => {
          const sticky = stickyFirstColumn && idx === 0
          const isNumeric = cell.column.columnDef.meta?.numeric === true
          return (
            <td
              key={cell.id}
              className={cn(
                'h-[var(--row-height)] overflow-hidden text-ellipsis whitespace-nowrap border-b border-border-subtle px-[var(--cell-padding-x)] py-[var(--cell-padding-y)] text-primary align-middle',
                isNumeric && 'text-right tabular-nums',
                sticky &&
                  'sticky left-0 z-base border-r border-r-border-default bg-surface group-hover:bg-surface-hover',
                sticky && isSelected && 'bg-accent-subtle',
                sticky &&
                  isCritical &&
                  '[box-shadow:inset_2px_0_0_var(--color-critical-left-border)]',
              )}
            >
              {flexRender(cell.column.columnDef.cell, cell.getContext())}
            </td>
          )
        })}
      </tr>
      {isExpanded ? (
        <tr data-expanded-row={row.id} className="bg-surface-sunk">
          <td colSpan={visibleCells.length} className="border-b border-border-default p-0">
            {renderExpandedRow!(row.original)}
          </td>
        </tr>
      ) : null}
    </Fragment>
  )
}

const GROUP_ACCENT_DOT: Record<GroupAccent, string> = {
  neutral: 'bg-border-strong',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

/**
 * A collapsible group: a full-width sticky header row (chevron + accent dot +
 * label + count) followed by the group's item rows. Header `<td>` spans every
 * leaf column so the divider runs edge-to-edge while the data columns below
 * stay aligned to the shared `<colgroup>`.
 */
function GroupRows({
  groupKey,
  label,
  accent,
  count,
  collapsed,
  colSpan,
  onToggle,
  children,
}: {
  groupKey: string
  label: string
  accent?: GroupAccent
  count: number
  collapsed: boolean
  colSpan: number
  onToggle: () => void
  children: React.ReactNode
}) {
  return (
    <>
      <tr data-group-header={groupKey} className="bg-surface-sunk">
        <td colSpan={colSpan} className="sticky left-0 z-base border-b border-border-default p-0">
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={!collapsed}
            className={cn(
              'flex w-full cursor-pointer items-center gap-2 border-none bg-transparent',
              'px-[var(--cell-padding-x)] py-1.5 text-left',
              'hover:bg-surface-hover focus:outline-none focus:shadow-focus',
            )}
          >
            <ChevronRight
              size={14}
              className={cn('shrink-0 text-tertiary transition-transform duration-fast', !collapsed && 'rotate-90')}
              aria-hidden="true"
            />
            {accent ? (
              <span
                className={cn('h-2 w-2 shrink-0 rounded-full', GROUP_ACCENT_DOT[accent])}
                aria-hidden="true"
              />
            ) : null}
            <span className="text-role-data-primary">{label}</span>
            <span className="text-role-badge-count text-tertiary">({count})</span>
          </button>
        </td>
      </tr>
      {children}
    </>
  )
}

// Augment TanStack's ColumnMeta so columns can opt into right-align +
// tabular-nums rendering without leaking style logic into each cell renderer.
declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData, TValue> {
    numeric?: boolean
  }
}
